/**
 * NextJS-compatible cross-origin messaging system
 * For development environments and cross-domain communication
 */

import {
  getDomainInfo,
  isAllowedOrigin,
  getPostMessageTargets,
} from "./domainDetection";

export interface CrossOriginMessage {
  type: "CROSS_DOMAIN_SESSION";
  action:
    | "SESSION_LOGIN"
    | "SESSION_LOGOUT"
    | "SESSION_REFRESH"
    | "SESSION_SYNC";
  data: string | null;
  timestamp: number;
  source: "nextjs-app" | "vite-app";
  target?: string;
}

export interface MessageHandler {
  (message: CrossOriginMessage): void;
}

export interface CrossOriginMessagingOptions {
  enableDebug?: boolean;
  allowedOrigins?: string[];
  enableAutoSync?: boolean;
  syncInterval?: number;
}

/**
 * Cross-origin messaging manager for NextJS
 * Handles postMessage communication with Vite app
 */
export class CrossOriginMessaging {
  private options: CrossOriginMessagingOptions;
  private messageHandlers: Set<MessageHandler>;
  private isServer: boolean;
  private syncInterval: NodeJS.Timeout | null = null;
  private isListening: boolean = false;

  constructor(options: CrossOriginMessagingOptions = {}) {
    this.options = {
      enableDebug: options.enableDebug ?? false,
      allowedOrigins: options.allowedOrigins || getPostMessageTargets(),
      enableAutoSync: options.enableAutoSync ?? true,
      syncInterval: options.syncInterval || 30000, // 30 seconds
      ...options,
    };
    this.messageHandlers = new Set();
    this.isServer = typeof window === "undefined";

    if (!this.isServer && this.options.enableAutoSync) {
      this.startAutoSync();
    }
  }

  /**
   * Start listening for cross-origin messages
   */
  startListening(): void {
    if (this.isServer || this.isListening) return;

    window.addEventListener("message", this.handleMessage.bind(this));
    this.isListening = true;

    if (this.options.enableDebug) {
      console.log("[CrossOriginMessaging] Started listening for messages");
    }
  }

  /**
   * Stop listening for cross-origin messages
   */
  stopListening(): void {
    if (this.isServer || !this.isListening) return;

    window.removeEventListener("message", this.handleMessage.bind(this));
    this.isListening = false;

    if (this.options.enableDebug) {
      console.log("[CrossOriginMessaging] Stopped listening for messages");
    }
  }

  /**
   * Add a message handler
   */
  addMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.add(handler);
  }

  /**
   * Remove a message handler
   */
  removeMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.delete(handler);
  }

  /**
   * Broadcast session update to other domains
   */
  broadcastSessionUpdate(
    action:
      | "SESSION_LOGIN"
      | "SESSION_LOGOUT"
      | "SESSION_REFRESH"
      | "SESSION_SYNC",
    data: string | null
  ): void {
    if (this.isServer) return;

    const message: CrossOriginMessage = {
      type: "CROSS_DOMAIN_SESSION",
      action,
      data,
      timestamp: Date.now(),
      source: "nextjs-app",
    };

    this.sendMessage(message);

    if (this.options.enableDebug) {
      console.log("[CrossOriginMessaging] Broadcasted session update:", action);
    }
  }

  /**
   * Send a message to specific targets
   */
  sendMessage(message: CrossOriginMessage, targets?: string[]): void {
    if (this.isServer) return;

    const targetOrigins = targets || this.options.allowedOrigins || [];

    targetOrigins.forEach((origin) => {
      try {
        // Send to parent window if in iframe
        if (window.parent !== window) {
          window.parent.postMessage(message, origin);
        }

        // Send to current window
        window.postMessage(message, origin);

        // Send to all iframes
        const iframes = document.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage(message, origin);
          }
        });
      } catch (error) {
        if (this.options.enableDebug) {
          console.error(
            "[CrossOriginMessaging] Error sending message to",
            origin,
            error
          );
        }
      }
    });
  }

  /**
   * Request session sync from other domains
   */
  requestSessionSync(): void {
    this.broadcastSessionUpdate("SESSION_SYNC", null);
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(event: MessageEvent): void {
    if (this.isServer) return;

    // Validate origin
    if (!isAllowedOrigin(event.origin)) {
      if (this.options.enableDebug) {
        console.warn(
          "[CrossOriginMessaging] Rejected message from unauthorized origin:",
          event.origin
        );
      }
      return;
    }

    // Validate message structure
    if (!this.isValidMessage(event.data)) {
      if (this.options.enableDebug) {
        console.warn(
          "[CrossOriginMessaging] Invalid message structure:",
          event.data
        );
      }
      return;
    }

    const message = event.data as CrossOriginMessage;

    // Don't process our own messages
    if (message.source === "nextjs-app") {
      return;
    }

    if (this.options.enableDebug) {
      console.log("[CrossOriginMessaging] Received message:", message);
    }

    // Notify all handlers
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        if (this.options.enableDebug) {
          console.error(
            "[CrossOriginMessaging] Error in message handler:",
            error
          );
        }
      }
    });
  }

  /**
   * Validate message structure
   */
  private isValidMessage(data: any): data is CrossOriginMessage {
    return (
      data &&
      typeof data === "object" &&
      data.type === "CROSS_DOMAIN_SESSION" &&
      typeof data.action === "string" &&
      typeof data.timestamp === "number" &&
      typeof data.source === "string"
    );
  }

  /**
   * Start automatic session synchronization
   */
  private startAutoSync(): void {
    if (this.syncInterval) return;

    this.syncInterval = setInterval(() => {
      this.requestSessionSync();
    }, this.options.syncInterval);

    if (this.options.enableDebug) {
      console.log(
        "[CrossOriginMessaging] Started auto-sync with interval:",
        this.options.syncInterval
      );
    }
  }

  /**
   * Stop automatic session synchronization
   */
  private stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;

      if (this.options.enableDebug) {
        console.log("[CrossOriginMessaging] Stopped auto-sync");
      }
    }
  }

  /**
   * Test connectivity with other domains
   */
  testConnectivity(): Promise<{ [origin: string]: boolean }> {
    return new Promise((resolve) => {
      if (this.isServer) {
        resolve({});
        return;
      }

      const results: { [origin: string]: boolean } = {};
      const targets = this.options.allowedOrigins || [];
      let completed = 0;

      const timeout = setTimeout(() => {
        resolve(results);
      }, 5000); // 5 second timeout

      const testHandler = (event: MessageEvent) => {
        if (
          event.data?.type === "CROSS_DOMAIN_SESSION" &&
          event.data?.action === "CONNECTIVITY_TEST"
        ) {
          results[event.origin] = true;
          completed++;

          if (completed >= targets.length) {
            clearTimeout(timeout);
            this.removeMessageHandler(testHandler);
            resolve(results);
          }
        }
      };

      this.addMessageHandler(testHandler);

      // Send test messages
      targets.forEach((origin) => {
        this.sendMessage(
          {
            type: "CROSS_DOMAIN_SESSION",
            action: "CONNECTIVITY_TEST",
            data: null,
            timestamp: Date.now(),
            source: "nextjs-app",
          },
          [origin]
        );
      });
    });
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): {
    isListening: boolean;
    handlerCount: number;
    allowedOrigins: string[];
    autoSyncEnabled: boolean;
    syncInterval: number;
  } {
    return {
      isListening: this.isListening,
      handlerCount: this.messageHandlers.size,
      allowedOrigins: this.options.allowedOrigins || [],
      autoSyncEnabled: this.options.enableAutoSync || false,
      syncInterval: this.options.syncInterval || 0,
    };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopListening();
    this.stopAutoSync();
    this.messageHandlers.clear();

    if (this.options.enableDebug) {
      console.log("[CrossOriginMessaging] Destroyed messaging instance");
    }
  }
}

/**
 * Create a cross-origin messaging instance
 */
export function createCrossOriginMessaging(
  options?: CrossOriginMessagingOptions
): CrossOriginMessaging {
  return new CrossOriginMessaging(options);
}

/**
 * Default cross-origin messaging instance
 */
export const crossOriginMessaging = createCrossOriginMessaging({
  enableDebug: process.env.NODE_ENV === "development",
});

/**
 * Session-specific messaging utilities
 */
export class SessionMessaging {
  private messaging: CrossOriginMessaging;
  private sessionKey: string;

  constructor(
    sessionKey: string = "SUPABASE_SESSION",
    options?: CrossOriginMessagingOptions
  ) {
    this.messaging = new CrossOriginMessaging(options);
    this.sessionKey = sessionKey;
    this.setupSessionHandlers();
  }

  /**
   * Setup session-specific message handlers
   */
  private setupSessionHandlers(): void {
    this.messaging.addMessageHandler((message) => {
      switch (message.action) {
        case "SESSION_LOGIN":
          this.handleSessionLogin(message);
          break;
        case "SESSION_LOGOUT":
          this.handleSessionLogout(message);
          break;
        case "SESSION_REFRESH":
          this.handleSessionRefresh(message);
          break;
        case "SESSION_SYNC":
          this.handleSessionSync(message);
          break;
      }
    });
  }

  /**
   * Handle session login from other domains
   */
  private handleSessionLogin(message: CrossOriginMessage): void {
    if (message.data) {
      try {
        const sessionData = JSON.parse(message.data);
        localStorage.setItem(this.sessionKey, message.data);

        // Dispatch custom event for components to listen to
        window.dispatchEvent(
          new CustomEvent("crossDomainSessionLogin", {
            detail: { sessionData, source: message.source },
          })
        );
      } catch (error) {
        console.error(
          "[SessionMessaging] Error handling session login:",
          error
        );
      }
    }
  }

  /**
   * Handle session logout from other domains
   */
  private handleSessionLogout(message: CrossOriginMessage): void {
    localStorage.removeItem(this.sessionKey);

    // Dispatch custom event for components to listen to
    window.dispatchEvent(
      new CustomEvent("crossDomainSessionLogout", {
        detail: { source: message.source },
      })
    );
  }

  /**
   * Handle session refresh from other domains
   */
  private handleSessionRefresh(message: CrossOriginMessage): void {
    if (message.data) {
      try {
        const sessionData = JSON.parse(message.data);
        localStorage.setItem(this.sessionKey, message.data);

        // Dispatch custom event for components to listen to
        window.dispatchEvent(
          new CustomEvent("crossDomainSessionRefresh", {
            detail: { sessionData, source: message.source },
          })
        );
      } catch (error) {
        console.error(
          "[SessionMessaging] Error handling session refresh:",
          error
        );
      }
    }
  }

  /**
   * Handle session sync request
   */
  private handleSessionSync(message: CrossOriginMessage): void {
    const currentSession = localStorage.getItem(this.sessionKey);
    if (currentSession) {
      this.messaging.broadcastSessionUpdate("SESSION_LOGIN", currentSession);
    }
  }

  /**
   * Broadcast session login
   */
  broadcastSessionLogin(sessionData: any): void {
    const sessionString = JSON.stringify(sessionData);
    this.messaging.broadcastSessionUpdate("SESSION_LOGIN", sessionString);
  }

  /**
   * Broadcast session logout
   */
  broadcastSessionLogout(): void {
    this.messaging.broadcastSessionUpdate("SESSION_LOGOUT", null);
  }

  /**
   * Broadcast session refresh
   */
  broadcastSessionRefresh(sessionData: any): void {
    const sessionString = JSON.stringify(sessionData);
    this.messaging.broadcastSessionUpdate("SESSION_REFRESH", sessionString);
  }

  /**
   * Request session sync
   */
  requestSessionSync(): void {
    this.messaging.requestSessionSync();
  }

  /**
   * Start listening
   */
  startListening(): void {
    this.messaging.startListening();
  }

  /**
   * Stop listening
   */
  stopListening(): void {
    this.messaging.stopListening();
  }

  /**
   * Destroy instance
   */
  destroy(): void {
    this.messaging.destroy();
  }
}

/**
 * Create a session messaging instance
 */
export function createSessionMessaging(
  sessionKey?: string,
  options?: CrossOriginMessagingOptions
): SessionMessaging {
  return new SessionMessaging(sessionKey, options);
}
